import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAppNumber } from '../feature/app.Slice';
import {
  getThemeColorsByAppNumber,
  getThemeNameByAppNumber,
  isValidAppNumber,
  LOCALHOST_THEME,
  resolveAppNumberFromEnvironment
} from '../config/appsConfig';

/**
 * Hook personnalisé pour gérer les thèmes dynamiques
 * Détermine le thème selon le hostname et applique les variables CSS
 * Synchronisé avec appNumber depuis Redux pour garantir la cohérence
 */

// Imports des styles SCSS (webpack les chargera au build time)
// Tous les styles sont importés pour être disponibles au runtime
import '../app/styles/app1/_index.scss';
import '../app/styles/app2/_index.scss';
import '../app/styles/app3/_index.scss';
import '../app/styles/app4/_index.scss';

export const useTheme = () => {
  const [theme, setTheme] = useState(null);
  const [isThemeLoaded, setIsThemeLoaded] = useState(false);
  const appNumber = useSelector(selectAppNumber);

  useEffect(() => {
    // Si appNumber est disponible depuis Redux, l'utiliser en priorité
    if (isValidAppNumber(appNumber)) {
      const detectedTheme = getThemeNameByAppNumber(appNumber);
      setTheme(detectedTheme);
      applyTheme(appNumber);
      setIsThemeLoaded(true);
      return;
    }

    const detectedAppNumber = resolveAppNumberFromEnvironment({
      hostname: window.location.hostname,
      port: window.location.port,
      search: window.location.search
    });

    const detectedTheme = getThemeNameByAppNumber(detectedAppNumber) || 'localhost';
    setTheme(detectedTheme);
    applyTheme(detectedAppNumber);
    setIsThemeLoaded(true);
  }, [appNumber]);

  const applyTheme = (appNumber) => {
    const root = document.documentElement;

    const colors = getThemeColorsByAppNumber(appNumber) || LOCALHOST_THEME;
    
    // Application des variables CSS
    Object.entries(colors).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });

    // Ajout d'une classe au body pour identifier le thème
    document.body.className = document.body.className.replace(/theme-\w+/g, '');
    const themeName = getThemeNameByAppNumber(appNumber) || 'localhost';
    document.body.classList.add(`theme-${themeName}`);
  };

  return {
    theme,
    isThemeLoaded,
    applyTheme
  };
};
